import React from 'react';
import { Clock, MapPin, Navigation, Tag, Truck } from 'lucide-react';
import { formatDeliveryHours } from '../../lib/deliveryHours';

// Shared optional public sections NOT already covered by BusinessExtras
// (BusinessExtras owns FAQ / reviews / contact form). These add working-hours,
// address+map and promotions to the reservations & menu templates.

// Working hours + address/map — rendered as one 2-column block (matches the
// business-card layout). Each half is independently gated by its module toggle.
export function HoursMapSection({ showHours, hours, showMap, address, lat, lng, order, deliveryHours }) {
  const delRows = formatDeliveryHours(deliveryHours);
  const hasHours = !!(showHours && (hours || delRows.length > 0));
  const mapHref = lat != null && lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;
  const hasMap = !!(showMap && (address || mapHref));
  if (!hasHours && !hasMap) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ order }} data-testid="hours-map-section">
      {hasHours && (
        <section className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 shadow-sm" data-testid="hours-section">
          <h3 className="text-[var(--text-primary)] font-semibold flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}>
            <Clock className="w-5 h-5 text-[var(--accent)]" /> Darba laiks
          </h3>
          {hours && <p className="text-[var(--text-muted)] whitespace-pre-line text-sm leading-relaxed">{hours}</p>}
          {delRows.length > 0 && (
            <div className={`${hours ? 'mt-4 pt-4 border-t border-[var(--border-card)]' : ''}`} data-testid="delivery-hours-display">
              <p className="text-[var(--text-primary)] text-sm font-semibold flex items-center gap-2 mb-1.5">
                <Truck className="w-4 h-4 text-[var(--accent)]" /> Ēdienu piegāde
              </p>
              <div className="space-y-0.5">
                {delRows.map((r, i) => (
                  <div key={i} className="flex justify-between gap-4 text-sm text-[var(--text-muted)]">
                    <span>{r.days}</span><span>{r.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
      {hasMap && (
        <section className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 shadow-sm" data-testid="address-section">
          <h3 className="text-[var(--text-primary)] font-semibold flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}>
            <MapPin className="w-5 h-5 text-[var(--accent)]" /> Atrašanās vieta
          </h3>
          {address && <p className="text-[var(--text-muted)] text-sm mb-4">{address}</p>}
          {mapHref && (
            <a href={mapHref} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold hover:bg-[var(--accent-hover)] transition-colors" data-testid="open-map-btn">
              <Navigation className="w-4 h-4" /> Atvērt kartē
            </a>
          )}
        </section>
      )}
    </div>
  );
}

export function PromotionsSection({ promotions, order }) {
  if (!(promotions?.length > 0)) return null;
  return (
    <section data-testid="promotions-section" style={{ order }}>
      <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Akcijas & piedāvājumi</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {promotions.map((p, i) => (
          <div key={i} className="rounded-2xl border border-[var(--accent-border)] p-5 shadow-sm" data-testid={`promotion-${i}`}
            style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }}>
            <Tag className="w-5 h-5 text-[var(--accent)] mb-2" />
            <p className="text-[var(--text-primary)] font-semibold">{p.title}</p>
            {p.description && <p className="text-[var(--text-faint)] text-sm mt-1">{p.description}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
